import { useEffect, useMemo, useState } from "react";
import {
  downloadEmployeePayslip,
  getEmployeePayrollOverview,
} from "../api/instateamPayrollApi";
import { AppIcon, StatusBanner } from "../components/InstateamEmployeeComponents";
import { formatCurrencyValue } from "../utils/employeeHelpers";
import {
  createSalaryOverviewDonutStyle,
  downloadTextDocument,
  formatFinancialYearLabel,
  getCurrentMonthKey,
  getFinancialYearOptions,
  getMonthFilterOptions,
  getPayrollStatusMeta,
  triggerBlobDownload,
} from "../utils/employeePayrollHelpers";

const EMPTY_METRICS = {
  netSalary: 0,
  grossSalary: 0,
  totalDeductions: 0,
  ctcAnnual: 0,
};

const EMPTY_SALARY_OVERVIEW = {
  financialYearLabel: "",
  grossSalary: 0,
  totalDeductions: 0,
  netSalary: 0,
};

const TABS = [
  { key: "payslips", label: "Payslips" },
  { key: "salaryDetails", label: "Salary Details" },
];

function SummaryCard({ icon, tone, label, value, helper }) {
  return (
    <article className="employee-payroll-metric-card">
      <div className={`employee-payroll-metric-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
    </article>
  );
}

function PayrollHistoryItem({ payslip, isActive, onSelect, onDownload }) {
  const statusMeta = getPayrollStatusMeta(payslip.status);
  const isDownloadDisabled =
    !payslip?._id || String(payslip._id).startsWith("preview-") || payslip.status !== "processed";

  return (
    <article className={`employee-payroll-history-item ${isActive ? "active" : ""}`}>
      <button
        type="button"
        className="employee-payroll-history-select"
        onClick={() => onSelect(payslip._id)}
      >
        <div className="employee-payroll-history-date">
          <strong>{payslip.monthLabel}</strong>
          <span>{payslip.paymentDateLabel || "Awaiting payment date"}</span>
        </div>

        <div className="employee-payroll-history-copy">
          <strong>{formatCurrencyValue(payslip.totals?.netSalary || 0)}</strong>
          <small>Net Salary</small>
        </div>

        <div className={`employee-status-pill ${statusMeta.tone}`}>
          {statusMeta.label}
        </div>
      </button>

      <button
        type="button"
        className="employee-portal-icon-button employee-payroll-download-button"
        onClick={(event) => {
          event.stopPropagation();
          onDownload(payslip);
        }}
        disabled={isDownloadDisabled}
      >
        <AppIcon name="download" />
      </button>
    </article>
  );
}

export default function EmployeePayrollPage({ token, searchText = "" }) {
  const monthKey = getCurrentMonthKey();
  const [activeTab, setActiveTab] = useState("payslips");
  const [selectedYear, setSelectedYear] = useState(() => formatFinancialYearLabel(monthKey));
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedPayslipId, setSelectedPayslipId] = useState("");
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getEmployeePayrollOverview(token, monthKey);

        if (!isActive) return;

        setOverview(payload.overview || null);
      } catch (error) {
        if (!isActive) return;

        setStatusMessage({
          type: "error",
          text: error.message,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [monthKey, token]);

  const payslips = useMemo(() => overview?.payslips || [], [overview?.payslips]);
  const financialYearOptions = useMemo(
    () => getFinancialYearOptions(payslips, monthKey),
    [monthKey, payslips]
  );

  useEffect(() => {
    if (!financialYearOptions.includes(selectedYear)) {
      setSelectedYear(financialYearOptions[0] || formatFinancialYearLabel(monthKey));
    }
  }, [financialYearOptions, monthKey, selectedYear]);

  const monthOptions = useMemo(
    () => getMonthFilterOptions(payslips, selectedYear),
    [payslips, selectedYear]
  );

  const filteredPayslips = useMemo(
    () =>
      payslips.filter((payslip) => {
        const matchesYear = payslip.financialYearLabel?.endsWith(selectedYear);
        const matchesMonth = selectedMonth === "all" || payslip.monthKey === selectedMonth;
        const query = String(searchText || "").trim().toLowerCase();
        const matchesSearch =
          !query ||
          [
            payslip.monthKey,
            payslip.monthLabel,
            payslip.financialYearLabel,
            payslip.status,
            payslip.statusLabel,
            payslip.fullName,
            payslip.employeeId,
          ]
            .join(" ")
            .toLowerCase()
            .includes(query);
        return matchesYear && matchesMonth && matchesSearch;
      }),
    [payslips, searchText, selectedMonth, selectedYear]
  );

  useEffect(() => {
    if (filteredPayslips.some((entry) => entry._id === selectedPayslipId)) {
      return;
    }

    const fallbackId =
      filteredPayslips[0]?._id || overview?.selectedPayslip?._id || "";
    setSelectedPayslipId(fallbackId);
  }, [filteredPayslips, overview?.selectedPayslip?._id, selectedPayslipId]);

  const selectedPayslip =
    filteredPayslips.find((entry) => entry._id === selectedPayslipId) ||
    overview?.selectedPayslip ||
    null;
  const summaryMetrics = selectedPayslip
    ? {
        netSalary: Number(selectedPayslip.totals?.netSalary || 0),
        grossSalary: Number(selectedPayslip.earnings?.grossSalary || 0),
        totalDeductions: Number(selectedPayslip.deductions?.totalDeductions || 0),
        ctcAnnual: Number(selectedPayslip.totals?.ctcAnnual || 0),
      }
    : overview?.metrics || EMPTY_METRICS;
  const salaryOverview = useMemo(
    () =>
      overview?.salaryOverview || {
        ...EMPTY_SALARY_OVERVIEW,
        financialYearLabel: `FY ${selectedYear}`,
      },
    [overview?.salaryOverview, selectedYear]
  );
  const salaryProfile = overview?.salaryProfile || {};
  const taxDetails = overview?.taxDetails || {};
  const donutStyle = useMemo(
    () => ({ background: createSalaryOverviewDonutStyle(salaryOverview) }),
    [salaryOverview]
  );

  async function handleDownloadPayslip(payslip = selectedPayslip) {
    if (!payslip?._id || String(payslip._id).startsWith("preview-")) {
      setStatusMessage({
        type: "warning",
        text: "Payslip will be available after payroll is processed by admin.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await downloadEmployeePayslip(token, payslip._id);
      triggerBlobDownload(payload.blob, payload.fileName);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDownloadStaticDocument(documentKey, documentLabel) {
    const referencePayslip = selectedPayslip || overview?.selectedPayslip || null;
    const content = [
      `Insta Team - ${documentLabel}`,
      "",
      `Employee: ${referencePayslip?.fullName || "Current Employee"}`,
      `Employee ID: ${referencePayslip?.employeeId || "-"}`,
      `Reference Month: ${referencePayslip?.monthLabel || "Current"}`,
      "",
      "This download was generated from the employee payroll panel.",
    ].join("\n");

    downloadTextDocument(`${documentKey}.txt`, content);
  }

  return (
    <section className="employee-portal-page employee-payroll-page">
      <div className="employee-page-heading employee-payroll-heading">
        <div>
          <h1>Salary & Payslips</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>></span>
            <span>Payroll</span>
            <span>></span>
            <span>Payslips</span>
          </div>
        </div>
      </div>

      {statusMessage ? (
        <StatusBanner
          type={statusMessage.type}
          text={statusMessage.text}
          onClose={() => setStatusMessage(null)}
        />
      ) : null}

      <div className="employee-payroll-overview-grid">
        <div className="employee-payroll-metrics-row">
          <SummaryCard
            icon="calendarCheck"
            tone="success"
            label={`Net Salary (${selectedPayslip?.monthLabel || "Current"})`}
            value={formatCurrencyValue(summaryMetrics.netSalary)}
            helper={selectedPayslip?.paymentDateLabel || "Awaiting payroll"}
          />
          <SummaryCard
            icon="wallet"
            tone="info"
            label="Gross Salary"
            value={formatCurrencyValue(summaryMetrics.grossSalary)}
            helper="View details"
          />
          <SummaryCard
            icon="calendar"
            tone="warning"
            label="Total Deductions"
            value={formatCurrencyValue(summaryMetrics.totalDeductions)}
            helper="View details"
          />
          <SummaryCard
            icon="documents"
            tone="violet"
            label="CTC (Annual)"
            value={formatCurrencyValue(summaryMetrics.ctcAnnual)}
            helper="View details"
          />
        </div>
      </div>

      <div className="employee-payroll-content-layout">
        <div className="employee-payroll-main-column">
          <article className="employee-profile-details-card employee-payroll-history-card">
            <div className="employee-profile-tab-row">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`employee-profile-tab ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "payslips" ? (
              <>
                <div className="employee-payroll-filter-row">
                  <label className="inline-select-field">
                    <select
                      value={selectedYear}
                      onChange={(event) => {
                        setSelectedYear(event.target.value);
                        setSelectedMonth("all");
                      }}
                    >
                      {financialYearOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <AppIcon name="chevronDown" />
                  </label>

                  <label className="inline-select-field">
                    <select
                      value={selectedMonth}
                      onChange={(event) => setSelectedMonth(event.target.value)}
                    >
                      <option value="all">All Months</option>
                      {monthOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <AppIcon name="chevronDown" />
                  </label>
                </div>

                <div className="employee-payroll-history-list">
                  {filteredPayslips.length ? (
                    filteredPayslips.map((payslip) => (
                      <PayrollHistoryItem
                        key={payslip._id}
                        payslip={payslip}
                        isActive={selectedPayslip?._id === payslip._id}
                        onSelect={setSelectedPayslipId}
                        onDownload={handleDownloadPayslip}
                      />
                    ))
                  ) : (
                    <div className="employee-coming-soon-card compact">
                      <h2>No payslips yet</h2>
                      <p>Payslips will appear here once payroll is processed by admin.</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="employee-payroll-salary-detail-grid">
                {[
                  ["Basic Salary", salaryProfile.basicSalary],
                  ["House Rent Allowance", salaryProfile.houseRentAllowance],
                  ["Conveyance Allowance", salaryProfile.conveyanceAllowance],
                  ["Special Allowance", salaryProfile.specialAllowance],
                  ["Gross Salary", salaryProfile.grossSalary],
                  ["Provident Fund", salaryProfile.providentFund],
                  ["Professional Tax", salaryProfile.professionalTax],
                  ["Income Tax / TDS", salaryProfile.incomeTax],
                  ["Total Deductions", salaryProfile.totalDeductions],
                  ["Net Salary", salaryProfile.netSalary],
                  ["CTC (Annual)", salaryProfile.ctcAnnual],
                  ["Pay Frequency", salaryProfile.payFrequency],
                ].map(([label, value]) => (
                  <div key={label} className="employee-profile-detail-item employee-payroll-detail-item">
                    <div className="employee-profile-detail-icon">
                      <AppIcon name="wallet" />
                    </div>
                    <div>
                      <span>{label}</span>
                      <strong>
                        {typeof value === "number" ? formatCurrencyValue(value) : value || "-"}
                      </strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>

          <article className="employee-profile-details-card employee-payroll-details-card">
            <div className="employee-payroll-detail-header">
              <div>
                <h3>Payslip Details</h3>
                <strong>{selectedPayslip?.monthLabel || "Current Month"}</strong>
                <p>{selectedPayslip?.paymentDateLabel || "Awaiting payment date"}</p>
              </div>

              <button
                className="secondary-button employee-inline-button"
                type="button"
                onClick={() => handleDownloadPayslip(selectedPayslip)}
                disabled={isSubmitting}
              >
                <AppIcon name="download" />
                Download Payslip
              </button>
            </div>

            <div className="employee-payroll-employee-card">
              <div className="employee-payroll-detail-grid">
                <div>
                  <span>Employee ID</span>
                  <strong>{selectedPayslip?.employeeId || "-"}</strong>
                </div>
                <div>
                  <span>Employee Name</span>
                  <strong>{selectedPayslip?.fullName || "-"}</strong>
                </div>
                <div>
                  <span>Designation</span>
                  <strong>{selectedPayslip?.designation || "-"}</strong>
                </div>
                <div>
                  <span>Department</span>
                  <strong>{selectedPayslip?.department || "-"}</strong>
                </div>
              </div>
            </div>

            <div className="employee-payroll-breakdown-card earnings">
              <div className="employee-payroll-breakdown-row heading">
                <strong>Earnings</strong>
                <span>Amount (₹)</span>
              </div>
              {[
                ["Basic Salary", selectedPayslip?.earnings?.basicSalary || 0],
                ["House Rent Allowance (HRA)", selectedPayslip?.earnings?.houseRentAllowance || 0],
                ["Conveyance Allowance", selectedPayslip?.earnings?.conveyanceAllowance || 0],
                ["Special Allowance", selectedPayslip?.earnings?.specialAllowance || 0],
              ].map(([label, value]) => (
                <div key={label} className="employee-payroll-breakdown-row">
                  <span>{label}</span>
                  <strong>{formatCurrencyValue(value)}</strong>
                </div>
              ))}
              <div className="employee-payroll-breakdown-row total success">
                <span>Gross Salary</span>
                <strong>{formatCurrencyValue(selectedPayslip?.earnings?.grossSalary || 0)}</strong>
              </div>
            </div>

            <div className="employee-payroll-breakdown-card deductions">
              <div className="employee-payroll-breakdown-row heading">
                <strong>Deductions</strong>
                <span>Amount (₹)</span>
              </div>
              {[
                ["Provident Fund (PF)", selectedPayslip?.deductions?.providentFund || 0],
                ["Professional Tax", selectedPayslip?.deductions?.professionalTax || 0],
                ["Income Tax (TDS)", selectedPayslip?.deductions?.incomeTax || 0],
              ].map(([label, value]) => (
                <div key={label} className="employee-payroll-breakdown-row">
                  <span>{label}</span>
                  <strong>{formatCurrencyValue(value)}</strong>
                </div>
              ))}
              <div className="employee-payroll-breakdown-row total danger">
                <span>Total Deductions</span>
                <strong>{formatCurrencyValue(selectedPayslip?.deductions?.totalDeductions || 0)}</strong>
              </div>
            </div>

            <div className="employee-payroll-net-card">
              <div className="employee-payroll-breakdown-row total info">
                <span>Net Salary</span>
                <strong>{formatCurrencyValue(selectedPayslip?.totals?.netSalary || 0)}</strong>
              </div>
              <small>Amount in words: {selectedPayslip?.amountInWords || "-"}</small>
            </div>
          </article>
        </div>

        <div className="employee-payroll-side-column">
          <article className="employee-payroll-right-card employee-payroll-overview-card">
            <div className="employee-side-heading">
              <h3>Salary Overview ({salaryOverview.financialYearLabel || `FY ${selectedYear}`})</h3>
            </div>
            <div className="employee-payroll-donut-shell">
              <div className="employee-payroll-donut" style={donutStyle}>
                <div className="employee-payroll-donut-inner">
                  <strong>{formatCurrencyValue(salaryOverview.netSalary)}</strong>
                  <span>Year to Date</span>
                </div>
              </div>

              <div className="employee-payroll-legend">
                <div>
                  <span className="gross" />
                  <strong>Gross Salary</strong>
                  <small>{formatCurrencyValue(salaryOverview.grossSalary)}</small>
                </div>
                <div>
                  <span className="deductions" />
                  <strong>Total Deductions</strong>
                  <small>{formatCurrencyValue(salaryOverview.totalDeductions)}</small>
                </div>
                <div>
                  <span className="net" />
                  <strong>Net Salary</strong>
                  <small>{formatCurrencyValue(salaryOverview.netSalary)}</small>
                </div>
              </div>
            </div>
          </article>

          <article className="employee-payroll-right-card">
            <div className="employee-side-heading">
              <h3>Tax Regime</h3>
            </div>

            <div className="employee-payroll-tax-grid">
              <div>
                <span>Tax Regime</span>
                <strong>{taxDetails.taxRegime || "-"}</strong>
              </div>
              <div>
                <span>PAN Number</span>
                <strong>{taxDetails.panNumber || "-"}</strong>
              </div>
              <div>
                <span>UAN Number</span>
                <strong>{taxDetails.uanNumber || "-"}</strong>
              </div>
              <div>
                <span>PF Number</span>
                <strong>{taxDetails.pfNumber || "-"}</strong>
              </div>
            </div>
          </article>

          <article className="employee-payroll-right-card">
            <div className="employee-side-heading">
              <h3>Important Downloads</h3>
            </div>

            <div className="employee-payroll-download-list">
              {(overview?.importantDownloads || []).map((document) => (
                <button
                  key={document.key}
                  type="button"
                  className="employee-payroll-download-item"
                  onClick={() => handleDownloadStaticDocument(document.key, document.label)}
                >
                  <span>{document.label}</span>
                  <AppIcon name="download" />
                </button>
              ))}
            </div>
          </article>
        </div>
      </div>

      {isLoading ? (
        <div className="employee-coming-soon-card compact">
          <h2>Loading payroll details...</h2>
          <p>Fetching salary records and payslip history.</p>
        </div>
      ) : null}
    </section>
  );
}
